import { useState, useCallback, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useInvoices, type InvoiceFilters } from '../../hooks/useInvoices'
import { supabase } from '../../api/supabase'
import InvoiceGenerator from '../../components/InvoiceGenerator'
import { useSyncToSage, formatSyncStatus } from '../../hooks/useSyncToSage'
import { useStickyHScroll } from '../../hooks/useStickyHScroll'
import { StickyHScrollbar } from '../../components/StickyHScrollbar'
import SageFailureDialog, { type SageFailureItem } from '../../components/SageFailureDialog'
import InvoiceDetailModal from '../../components/InvoiceDetailModal'
import CostInvoicesTab from '../../components/SupplierInvoices/CostInvoicesTab'
import { STATUS_COLORS, fmt, fmtDate } from '../../lib/invoiceFormat'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Job {
  id: string
  job_ref: string
  client_ref?: string
  direction: string
  transport_mode: string
  status: string
}

// ─── Status helpers ───────────────────────────────────────────────────────────

// No payment-tracking status (SENT/PAID/OVERDUE) exists on this table; if Ada needs to track
// paid vs. unpaid, that's separate, not-yet-built work, not something this page can show today.
const STATUSES = ['DRAFT', 'ISSUED', 'VOID', 'CREDITED']

// ─── Job picker modal ─────────────────────────────────────────────────────────

function JobPickerModal({ onSelect, onClose }: { onSelect: (job: Job) => void; onClose: () => void }) {
  const [jobs, setJobs] = useState<Job[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  const fetchJobs = useCallback(async (q: string) => {
    setLoading(true)
    // No "already invoiced" filter -- a job can carry multiple invoices now (Charges Phase 1:
    // freight to the importer, delivery to the consignee, etc. as separate invoices), so hiding
    // previously-invoiced jobs here would be wrong. (The old `.neq('status', 'INVOICED')` never
    // matched anything anyway -- 'INVOICED' isn't a valid jobs.status value.)
    let query = supabase
      .from('jobs')
      .select('id, job_ref, client_ref, direction, transport_mode, status')
      .or('import_source.is.null,import_source.neq.GROUP')   // a group's shipments are not billed: only the group is offered
      .order('created_at', { ascending: false })
      .limit(50)
    if (q.trim()) query = query.ilike('job_ref', `%${q}%`)
    const { data } = await query
    setJobs(data || [])
    setLoading(false)
  }, [])

  useState(() => { fetchJobs('') })

  const handleSearch = (v: string) => {
    setSearch(v)
    fetchJobs(v)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Select Job to Invoice</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700">
          <input
            autoFocus
            type="text"
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Search by Job Ref…"
            className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          />
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="py-8 text-center text-gray-400 text-sm">Loading…</div>
          ) : jobs.length === 0 ? (
            <div className="py-8 text-center text-gray-400 text-sm">No uninvoiced jobs found</div>
          ) : (
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {jobs.map(j => (
                <li key={j.id}>
                  <button
                    onClick={() => onSelect(j)}
                    className="w-full px-6 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between"
                  >
                    <div>
                      <span className="font-mono font-bold text-blue-600 dark:text-blue-400">{j.job_ref}</span>
                      {j.client_ref && <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">({j.client_ref})</span>}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {j.direction} · {j.transport_mode}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main Accounts Page
// ═══════════════════════════════════════════════════════════════════════════════

export default function Accounts() {
  const [filters, setFilters] = useState<InvoiceFilters>({})
  const { invoices, loading, error, fetchInvoices, updateInvoice } = useInvoices(filters)
  const { scrollProps: invoicesScrollProps, contentRef: invoicesTableRef, bar: invoicesScrollBar } = useStickyHScroll([invoices])

  const [showJobPicker, setShowJobPicker] = useState(false)
  const [selectedJob, setSelectedJob] = useState<Job | null>(null)
  const [viewingInvoice, setViewingInvoice] = useState<any | null>(null)
  const { syncInvoice, isSyncingInvoice } = useSyncToSage()
  const [bulkSyncingSage, setBulkSyncingSage] = useState(false)
  const [sageSyncErrors, setSageSyncErrors] = useState<Record<string, string>>({})
  // Z86: multi-select for Sync Selected. Only un-synced invoices are selectable (already-synced
  // rows have a greyed-out checkbox and are never added by Select All).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [syncFailures, setSyncFailures] = useState<{ items: SageFailureItem[]; total: number } | null>(null)

  // Z73.16 Part 4: deep-link from ECustomsPreview's "Create Invoice Draft" -- auto-opens the
  // just-created invoice's detail modal instead of leaving Ada to find it in the list herself.
  const [searchParams, setSearchParams] = useSearchParams()
  useEffect(() => {
    const invoiceId = searchParams.get('invoice')
    if (!invoiceId || loading) return
    const match = invoices.find(i => i.id === invoiceId)
    if (match) {
      // One-shot: open the deep-linked invoice once its data has arrived, then strip the URL
      // param so it doesn't re-trigger (e.g. on a later unrelated invoices refetch).
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setViewingInvoice(match)
      setSearchParams(prev => { const p = new URLSearchParams(prev); p.delete('invoice'); return p }, { replace: true })
    }
  }, [searchParams, invoices, loading, setSearchParams])

  // ── KPIs ──────────────────────────────────────────────────────────────────
  // No payment-tracking status exists on invoices (see STATUS_COLORS comment) -- these reflect
  // the real DRAFT/ISSUED/VOID lifecycle only, not paid/outstanding/overdue.
  const kpis = {
    issuedTotal: invoices
      .filter(i => i.status === 'ISSUED')
      .reduce((s, i) => s + (i.total_amount || 0), 0),
    issuedMonth: invoices
      .filter(i => i.status === 'ISSUED' && new Date(i.invoice_date).getMonth() === new Date().getMonth())
      .reduce((s, i) => s + (i.total_amount || 0), 0),
    draft: invoices.filter(i => i.status === 'DRAFT').length,
    void: invoices.filter(i => i.status === 'VOID').length,
  }

  const handleStatusChange = async (invoiceId: string, status: string) => {
    await updateInvoice(invoiceId, { status: status as any })
    await fetchInvoices()
  }

  const handleNotesChange = async (invoiceId: string, notes: string) => {
    await updateInvoice(invoiceId, { notes: notes || null })
  }

  const handleJobSelected = (job: Job) => {
    setShowJobPicker(false)
    setSelectedJob(job)
  }

  const handleInvoiceCreated = async () => {
    setSelectedJob(null)
    await fetchInvoices()
  }

  const handleSyncToSage = async (invoiceId: string) => {
    const result = await syncInvoice(invoiceId)
    setSageSyncErrors(prev => {
      const next = { ...prev }
      if (result.success) delete next[invoiceId]
      else next[invoiceId] = result.error || 'Sync failed'
      return next
    })
    if (result.success) fetchInvoices()
    return result
  }

  // Shared by Sync Selected and Sync All: runs the same one-by-one sync, then shows the PR #91
  // failure dialog (plain-English reasons via explainSageError) if anything failed.
  const syncMany = async (ids: string[]) => {
    setBulkSyncingSage(true)
    const items: SageFailureItem[] = []
    for (const id of ids) {
      const result = await handleSyncToSage(id)
      if (!result.success) {
        const inv = invoices.find(i => i.id === id)
        items.push({
          key: id,
          ref: inv?.invoice_no || id,
          party: inv?.customer?.name || '',
          detail: inv ? fmt(inv.total_amount || 0, inv.currency) : undefined,
          error: result.error,
        })
      }
    }
    setBulkSyncingSage(false)
    setSelectedIds(new Set())
    if (items.length > 0) setSyncFailures({ items, total: ids.length })
  }

  const handleBulkSyncToSage = () => syncMany(invoices.filter(i => !i.sage_invoice_id).map(i => i.id))
  const handleSyncSelected = () => syncMany(invoices.filter(i => selectedIds.has(i.id) && !i.sage_invoice_id).map(i => i.id))

  const selectableIds = invoices.filter(i => !i.sage_invoice_id).map(i => i.id)
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selectedIds.has(id))
  const toggleSelectAll = () => setSelectedIds(allSelected ? new Set() : new Set(selectableIds))
  const toggleSelected = (id: string) => setSelectedIds(prev => {
    const next = new Set(prev)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })

  const setFilter = (k: keyof InvoiceFilters, v: string) =>
    setFilters(prev => ({ ...prev, [k]: v || undefined }))

  // Z86: Sales invoices (default) | Cost invoices (supplier invoices + allocation to job costs).
  const tab: 'sales' | 'cost' = searchParams.get('tab') === 'cost' ? 'cost' : 'sales'
  const setTab = (t: 'sales' | 'cost') =>
    setSearchParams(prev => { const p = new URLSearchParams(prev); if (t === 'cost') p.set('tab', 'cost'); else { p.delete('tab'); p.delete('supplier_invoice') } return p }, { replace: true })
  const tabBar = (
    <div className="mb-6 flex gap-1 border-b border-gray-200 dark:border-gray-700">
      {([['sales', 'Sales invoices'], ['cost', 'Cost invoices']] as const).map(([k, label]) => (
        <button key={k} onClick={() => setTab(k)}
          className={`px-4 py-2 text-sm font-medium ${tab === k ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}>
          {label}
        </button>
      ))}
    </div>
  )

  if (tab === 'cost') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4 py-8">
          <div className="mb-6">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">Accounts</h1>
            <p className="text-gray-600 dark:text-gray-300">Supplier invoices and allocation to job costs</p>
          </div>
          {tabBar}
          <CostInvoicesTab initialInvoiceId={searchParams.get('supplier_invoice')} />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">

        {/* Header */}
        <div className="mb-8 flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">Accounts</h1>
            <p className="text-gray-600 dark:text-gray-300">Sales invoices, payment tracking and financial overview</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleSyncSelected}
              disabled={bulkSyncingSage || selectedIds.size === 0}
              title="Sync only the ticked invoices to Sage"
              className="px-4 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium disabled:opacity-50"
            >
              Sync Selected ({selectedIds.size})
            </button>
            <button
              onClick={handleBulkSyncToSage}
              disabled={bulkSyncingSage || invoices.length === 0}
              title="Sync every un-synced invoice on this page to Sage"
              className="px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium disabled:opacity-50"
            >
              {bulkSyncingSage ? 'Syncing to Sage…' : 'Sync All to Sage'}
            </button>
            <Link
              to="/accounts/sage-import"
              title="Import a BoxTop invoice export CSV directly via the API -- required for overseas companies"
              className="px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium"
            >
              Bulk Import Invoices
            </Link>
            <button
              onClick={() => setShowJobPicker(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 font-medium"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Invoice
            </button>
          </div>
        </div>

        {tabBar}

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Issued Total</p>
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{fmt(kpis.issuedTotal)}</p>
            <p className="text-xs text-gray-400 mt-1">All issued invoices</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Issued This Month</p>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">{fmt(kpis.issuedMonth)}</p>
            <p className="text-xs text-gray-400 mt-1">By invoice date</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Drafts</p>
            <p className="text-2xl font-bold text-gray-600 dark:text-gray-300">{kpis.draft}</p>
            <p className="text-xs text-gray-400 mt-1">Awaiting issue</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-5">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Void</p>
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">{kpis.void}</p>
            <p className="text-xs text-gray-400 mt-1">Voided invoices</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-5 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[180px]">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
              <input
                type="text"
                placeholder="Invoice number…"
                value={filters.search || ''}
                onChange={e => setFilter('search', e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="min-w-[140px]">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
              <select
                value={filters.status || ''}
                onChange={e => setFilter('status', e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">All Statuses</option>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="min-w-[140px]">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">From</label>
              <input
                type="date"
                value={filters.date_from || ''}
                onChange={e => setFilter('date_from', e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="min-w-[140px]">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">To</label>
              <input
                type="date"
                value={filters.date_to || ''}
                onChange={e => setFilter('date_to', e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <button
              onClick={() => setFilters({})}
              className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Clear
            </button>
          </div>
        </div>

        {/* Invoice table */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
              <p className="mt-3 text-gray-500 dark:text-gray-400 text-sm">Loading invoices…</p>
            </div>
          ) : error ? (
            <div className="p-8 text-center">
              <p className="text-red-600 dark:text-red-400 font-medium">Error loading invoices</p>
              <p className="text-sm text-gray-500 mt-1">{error}</p>
              <p className="text-xs text-gray-400 mt-3">The invoices table may not exist yet. Create your first invoice using a Job to initialise it.</p>
            </div>
          ) : invoices.length === 0 ? (
            <div className="p-12 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">No invoices yet</h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {Object.values(filters).some(Boolean)
                  ? 'No invoices match the current filters.'
                  : 'Click "New Invoice" to generate your first invoice from a job.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto" {...invoicesScrollProps}>
              <table ref={invoicesTableRef as React.RefObject<HTMLTableElement>} className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                  <tr>
                    <th className="px-4 py-3 w-8">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        disabled={selectableIds.length === 0 || bulkSyncingSage}
                        onChange={toggleSelectAll}
                        title="Select all un-synced invoices"
                        aria-label="Select all un-synced invoices"
                      />
                    </th>
                    {['Invoice No', 'Job', 'Customer', 'Invoice Date', 'Due Date', 'Amount', 'VAT', 'Total', 'Status', 'Actions'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {invoices.map(inv => (
                    <tr
                      key={inv.id}
                      onClick={() => setViewingInvoice(inv)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                    >
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(inv.id)}
                          disabled={!!inv.sage_invoice_id || bulkSyncingSage}
                          onChange={() => toggleSelected(inv.id)}
                          title={inv.sage_invoice_id ? 'Already synced to Sage' : undefined}
                          aria-label={`Select invoice ${inv.invoice_no}`}
                        />
                      </td>
                      <td className="px-4 py-3 font-mono font-bold text-blue-600 dark:text-blue-400">
                        {inv.invoice_no}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {inv.job?.job_ref || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-200">
                        <div className="font-medium">{inv.customer?.name || '—'}</div>
                        {inv.customer?.code && (
                          <div className="text-xs text-gray-400">{inv.customer.code}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {fmtDate(inv.invoice_date)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-400">
                        {fmtDate(inv.due_date)}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300 text-right">
                        {fmt(inv.amount || 0, inv.currency)}
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-right">
                        {fmt(inv.vat_amount || 0, inv.currency)}
                      </td>
                      <td className="px-4 py-3 font-bold text-gray-900 dark:text-white text-right">
                        {fmt(inv.total_amount || 0, inv.currency)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[inv.status] || 'bg-gray-100 text-gray-700'}`}>
                          {inv.status}
                        </span>
                      </td>
                      <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                        <div className="flex gap-2">
                          {inv.status === 'DRAFT' && (
                            <button
                              onClick={() => handleStatusChange(inv.id, 'ISSUED')}
                              className="text-xs text-blue-600 hover:underline font-medium"
                            >
                              Issue
                            </button>
                          )}
                          <button
                            onClick={() => setViewingInvoice(inv)}
                            className="text-xs text-gray-500 hover:underline"
                          >
                            View
                          </button>
                          {!inv.sage_invoice_id && (
                            <button
                              onClick={() => handleSyncToSage(inv.id)}
                              disabled={isSyncingInvoice(inv.id)}
                              className="text-xs text-teal-600 hover:underline font-medium disabled:opacity-50"
                            >
                              {isSyncingInvoice(inv.id) ? 'Syncing…' : 'Sync to Sage'}
                            </button>
                          )}
                        </div>
                        <div className="mt-0.5 text-xs">
                          {sageSyncErrors[inv.id] ? (
                            <span className="text-red-600 dark:text-red-400" title={sageSyncErrors[inv.id]}>Sage sync error</span>
                          ) : (
                            <span className="text-gray-400">{inv.sage_invoice_id ? formatSyncStatus(inv.sage_last_synced_at) : 'Not synced'}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {!loading && !error && invoices.length > 0 && <StickyHScrollbar {...invoicesScrollBar} />}

        {!loading && !error && (
          <p className="mt-3 text-xs text-gray-400">{invoices.length} invoice{invoices.length !== 1 ? 's' : ''}</p>
        )}
      </div>

      {/* Job picker */}
      {showJobPicker && (
        <JobPickerModal
          onSelect={handleJobSelected}
          onClose={() => setShowJobPicker(false)}
        />
      )}

      {/* Invoice generator */}
      {selectedJob && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="my-auto w-full max-w-4xl">
            <InvoiceGenerator
              jobId={selectedJob.id}
              onInvoiceCreated={handleInvoiceCreated}
              onClose={() => setSelectedJob(null)}
            />
          </div>
        </div>
      )}

      {/* Invoice detail */}
      {viewingInvoice && (
        <InvoiceDetailModal
          invoice={viewingInvoice}
          onClose={() => setViewingInvoice(null)}
          onStatusChange={handleStatusChange}
          onNotesChange={handleNotesChange}
          onChanged={fetchInvoices}
        />
      )}
      {syncFailures && (
        <SageFailureDialog
          failCount={syncFailures.items.length}
          total={syncFailures.total}
          successCount={syncFailures.total - syncFailures.items.length}
          noun="invoice"
          items={syncFailures.items}
          onClose={() => setSyncFailures(null)}
        />
      )}
    </div>
  )
}
